import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ImportDedupeMode, ImportEntityType, ImportJobResponse, ImportMapping } from '@orion/contracts';
import { Button, Card } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useLeadStatusOptionsQuery } from '../leads/api.js';
import { usePipelinesQuery } from '../deals/api.js';
import { useCommitImport, useImportJobQuery, useRollbackImport, useUploadImport, useValidateImport } from './api.js';

const LEAD_FIELD_OPTIONS: Array<{ value: ImportMapping[string]; label: string }> = [
  { value: 'skip', label: "Don't import" },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'companyName', label: 'Company' },
  { value: 'jobTitle', label: 'Job title' },
  { value: 'website', label: 'Website' },
  { value: 'sourceName', label: 'Lead source' },
  { value: 'note', label: 'Note' },
];

const OPPORTUNITY_FIELD_OPTIONS: Array<{ value: ImportMapping[string]; label: string }> = [
  { value: 'skip', label: "Don't import" },
  { value: 'name', label: 'Opportunity name' },
  { value: 'amount', label: 'Amount' },
  { value: 'currency', label: 'Currency' },
  { value: 'pipelineName', label: 'Pipeline' },
  { value: 'stageName', label: 'Stage' },
  { value: 'companyName', label: 'Company' },
  { value: 'ownerName', label: 'Owner' },
  { value: 'expectedCloseDate', label: 'Expected close date' },
  { value: 'contactName', label: 'Contact name' },
  { value: 'contactEmail', label: 'Contact email' },
  { value: 'contactTitle', label: 'Contact title' },
  { value: 'source', label: 'Source' },
  { value: 'status', label: 'Status' },
  { value: 'note', label: 'Note' },
];

const LEAD_SAMPLE_ROW: Record<string, string> = {
  'First name': 'Jane',
  'Last name': 'Doe',
  Email: 'jane@example.com',
  Phone: '+1 555 0100',
  Company: 'Example Co',
  'Job title': 'VP Sales',
  Website: 'example.com',
  'Lead source': 'Referral',
  Note: 'Met at conference, interested in the platform plan.',
};

const OPPORTUNITY_SAMPLE_ROW: Record<string, string> = {
  'Opportunity name': 'Example Co — Platform rollout',
  Amount: '15000',
  Currency: 'USD',
  Pipeline: 'Active Pipeline',
  Stage: 'Proposal Sent',
  Company: 'Example Co',
  Owner: 'Jane Rep',
  'Expected close date': '2026-12-31',
  'Contact name': 'John Smith',
  'Contact email': 'john@example.com',
  'Contact title': 'Procurement Lead',
  Source: 'Referral',
  Status: 'open',
  Note: 'Sent proposal, awaiting response.',
};

function csvField(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Headers are the field labels this entity actually understands (never
 * 'skip') so the file downloads already shaped for `suggestMapping()`'s
 * keyword matcher to auto-map every column — "what good input looks like"
 * doubles as living documentation, instead of a hand-maintained doc page
 * that drifts from what the importer actually accepts. */
function downloadSampleCsv(isOpportunity: boolean) {
  const options = (isOpportunity ? OPPORTUNITY_FIELD_OPTIONS : LEAD_FIELD_OPTIONS).filter((o) => o.value !== 'skip');
  const sample = isOpportunity ? OPPORTUNITY_SAMPLE_ROW : LEAD_SAMPLE_ROW;
  const headers = options.map((o) => o.label);
  const row = options.map((o) => csvField(sample[o.label] ?? ''));
  const csv = `${headers.map(csvField).join(',')}\n${row.join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = isOpportunity ? 'orion-opportunities-sample.csv' : 'orion-leads-sample.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const LEAD_DEDUPE_OPTIONS: Array<{ value: ImportDedupeMode; label: string; help: string }> = [
  { value: 'skip', label: 'Skip duplicates', help: 'Keep existing leads untouched' },
  { value: 'update', label: 'Update existing', help: 'Fill empty fields, never overwrite owner' },
  { value: 'create_anyway', label: 'Create anyway', help: 'Always create a new lead' },
];

const OPPORTUNITY_DEDUPE_OPTIONS: Array<{ value: ImportDedupeMode; label: string; help: string }> = [
  { value: 'skip', label: 'Skip duplicates', help: 'Keep existing opportunities untouched' },
  { value: 'update', label: 'Update existing', help: 'Fill empty fields, never overwrite owner' },
  { value: 'create_anyway', label: 'Create anyway', help: 'Always create a new opportunity' },
];

type Step = 'upload' | 'map' | 'review' | 'done';

/** docs section 23 "Import / Export" — upload → map columns → review &
 * validate → import, matching the design's 4-step indicator exactly:
 * "Review & validate" is a real dry run (apps/api/.../imports.service.ts
 * #validate runs every row through the same required-field and email-
 * dedupe checks `commit` does, inside a transaction that's always rolled
 * back) rather than a static illustration of what commit might find.
 *
 * Shared by both `/leads/import` (entityType='lead', the default) and
 * `/deals/import` (entityType='opportunity') — the column-mapping field
 * list, dedupe wording, default-owner/status-vs-pipeline selector, and the
 * post-import destination all switch on `entityType`; everything else
 * (upload, step indicator, review table, rollback) is identical for both.
 *
 * Column mapping stays fixed to each entity's own backend-understood field
 * set — no custom-field targets, no multi-select/delimiter-split mapping,
 * no per-value "map values…" sub-dialog, all real gaps the design's example
 * (Product interest, a currency-typed Budget field, a Source value-mapper)
 * shows but nothing in this codebase reads or writes yet. Default owner is
 * a single rep, not a round-robin strategy; duplicate matching is by email
 * (lead) or name+company (opportunity) only, not a fuzzier match; one tag,
 * not several, and tagging only applies to leads — every one of those is a
 * real backend capability this pass didn't add, not a UI omission. */
export function ImportPage({ entityType = 'lead' }: { entityType?: ImportEntityType }) {
  const isOpportunity = entityType === 'opportunity';
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = useUploadImport();
  const validateImport = useValidateImport();
  const commit = useCommitImport();
  const rollback = useRollbackImport();
  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);
  const { data: statuses } = useLeadStatusOptionsQuery();
  const { data: pipelines } = usePipelinesQuery();

  const [job, setJob] = useState<ImportJobResponse | null>(null);
  const [mapping, setMapping] = useState<ImportMapping>({});
  const [dedupeMode, setDedupeMode] = useState<ImportDedupeMode>('skip');
  const [defaultOwnerId, setDefaultOwnerId] = useState('');
  const [defaultStatusId, setDefaultStatusId] = useState('');
  const [defaultPipelineId, setDefaultPipelineId] = useState('');
  const [tag, setTag] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('upload');

  const { data: detail } = useImportJobQuery(step === 'review' || step === 'done' ? job?.id : undefined);

  const FIELD_OPTIONS = isOpportunity ? OPPORTUNITY_FIELD_OPTIONS : LEAD_FIELD_OPTIONS;
  const DEDUPE_OPTIONS = isOpportunity ? OPPORTUNITY_DEDUPE_OPTIONS : LEAD_DEDUPE_OPTIONS;
  const options = {
    dedupeMode,
    defaultOwnerId: defaultOwnerId || undefined,
    defaultStatusId: isOpportunity ? undefined : defaultStatusId || undefined,
    defaultPipelineId: isOpportunity ? defaultPipelineId || undefined : undefined,
    tag: tag || undefined,
  };

  const onFile = async (file: File) => {
    setError(null);
    try {
      const result = await upload.mutateAsync({ file, entityType });
      setJob(result);
      setMapping((result.suggestedMapping as ImportMapping) ?? {});
      setStep('map');
    } catch {
      setError('Could not read that file — make sure it is a CSV under 5 MB.');
    }
  };

  // design/orion-crm-design/rendered-html/Import.html captures step 2 (Map
  // columns) already populated from an upload — `?__design=Import` (dev-
  // only) does a real upload of a dummy file against the fixture mock, the
  // same call a real drop would make, rather than hand-assembling state.
  useEffect(() => {
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'Import') {
      void onFile(new File(['placeholder'], 'tradeshow-leads-sept.csv', { type: 'text/csv' }));
    }
  }, []);

  const onContinueToReview = async () => {
    if (!job) return;
    setError(null);
    try {
      await validateImport.mutateAsync({ id: job.id, input: { mapping, options } });
      setStep('review');
    } catch {
      setError('Could not validate that file. Please try again.');
    }
  };

  const onImport = async () => {
    if (!job) return;
    setError(null);
    try {
      const result = await commit.mutateAsync({ id: job.id, input: { mapping, options } });
      setJob(result);
      setStep('done');
    } catch {
      setError('The import could not be completed. Please try again.');
    }
  };

  const mappedCount = Object.values(mapping).filter((f) => f !== 'skip').length;
  const skippedCount = Object.values(mapping).length - mappedCount;
  const counts = detail?.counts;

  return (
    <>
      <PageHeader breadcrumb={isOpportunity ? [{ label: 'Opportunities', to: '/deals' }, { label: 'Import' }] : [{ label: 'Leads', to: '/leads' }, { label: 'Import' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-hidden px-7 py-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">{isOpportunity ? 'Import opportunities' : 'Import leads'}</h1>
          <p className="text-[13px] text-text-muted">Match your spreadsheet columns to Orion fields. Nothing is saved until the final step.</p>
        </div>

        <ol aria-label="Import steps" className="flex shrink-0 items-center gap-2 text-sm font-medium">
          {(
            [
              ['upload', '1', 'Upload'],
              ['map', '2', 'Map columns'],
              ['review', '3', 'Review & validate'],
              ['done', '4', 'Import'],
            ] as const
          ).map(([key, num, label], i) => {
            const order: Step[] = ['upload', 'map', 'review', 'done'];
            const isDone = order.indexOf(step) > i;
            const isCurrent = step === key;
            return (
              <li key={key} className="flex items-center gap-2">
                {i > 0 && <span className="h-px w-6 bg-divider" />}
                <span
                  className={`flex size-5.5 items-center justify-center rounded-full text-xs font-semibold ${isDone ? 'bg-success text-white' : isCurrent ? 'bg-primary text-white' : 'bg-divider text-text-muted'}`}
                >
                  {isDone ? '✓' : num}
                </span>
                <span className={isCurrent ? 'text-ink' : isDone ? 'text-text-secondary' : 'text-text-muted'}>{label}</span>
              </li>
            );
          })}
        </ol>

        {error && (
          <div role="alert" className="shrink-0 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        {step === 'upload' && (
          <Card className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-sm text-text-muted">{isOpportunity ? 'Upload a CSV of opportunities to import.' : 'Upload a CSV of leads to import.'}</p>
            <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            <Button onClick={() => fileInput.current?.click()} loading={upload.isPending}>
              Choose file…
            </Button>
            <button type="button" onClick={() => downloadSampleCsv(isOpportunity)} className="text-xs font-semibold text-primary-text hover:underline">
              Download a sample CSV
            </button>
          </Card>
        )}

        {step === 'map' && job && (
          <div className="grid min-h-0 flex-1 grid-cols-[1fr_300px] gap-4 overflow-hidden">
            <Card className="flex min-h-0 flex-col overflow-hidden p-0">
              <div className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-3 text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 9h6M9 13h6M9 17h4" />
                </svg>
                <span className="font-semibold text-ink">{job.fileName}</span>
                <span className="text-text-muted">
                  · {job.counts.total} rows{job.counts.skipped > 0 ? ` (${job.counts.skipped} blank rows already skipped)` : ''} · {Object.keys(mapping).length} columns
                </span>
                <div className="flex-1" />
                <span className="text-xs text-text-muted">
                  {mappedCount} mapped · {skippedCount} skipped
                </span>
              </div>
              <div className="grid shrink-0 grid-cols-[1fr_1fr_40px_1fr_100px] gap-3 border-b border-divider bg-surface-subtle px-5 py-2 text-xs font-semibold text-text-muted">
                <span>CSV column</span>
                <span>Sample value</span>
                <span />
                <span>Orion field</span>
                <span>Status</span>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {Object.entries(mapping).map(([header, field]) => (
                  <div key={header} className="grid grid-cols-[1fr_1fr_40px_1fr_100px] items-center gap-3 border-b border-divider px-5 py-2.5 text-sm">
                    <span className="truncate font-semibold text-ink">{header}</span>
                    <span className="truncate text-text-secondary">{job.previewRows?.[0]?.[header] ?? '—'}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A9FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                    <select
                      value={field}
                      onChange={(e) => setMapping((m) => ({ ...m, [header]: e.target.value as ImportMapping[string] }))}
                      className="h-8 rounded-control border border-border-strong bg-surface px-2 text-xs"
                    >
                      {FIELD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {field === 'skip' ? (
                      <span className="flex h-5.5 w-fit items-center rounded-[6px] bg-neutral-pill-soft px-2 text-xs font-semibold text-text-secondary">Skipped</span>
                    ) : (
                      <span className="flex h-5.5 w-fit items-center rounded-[6px] bg-success-soft px-2 text-xs font-semibold text-success">Mapped</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card className="flex flex-col gap-4 overflow-y-auto p-4">
              <div className="flex flex-col gap-2">
                <span className="text-[13px] font-semibold text-ink">Duplicate handling</span>
                {DEDUPE_OPTIONS.map((o) => (
                  <label key={o.value} className="flex cursor-pointer items-start gap-2 text-[13px]">
                    <input type="radio" name="dupes" checked={dedupeMode === o.value} onChange={() => setDedupeMode(o.value)} className="mt-0.5" />
                    <span className="flex flex-col">
                      <span className="font-medium text-ink">{o.label}</span>
                      <span className="text-xs text-text-muted">{o.help}</span>
                    </span>
                  </label>
                ))}
                <span className="text-xs text-text-muted">{isOpportunity ? 'Matched on name + company' : 'Matched on email'}</span>
              </div>

              <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
                Default owner
                <select value={defaultOwnerId} onChange={(e) => setDefaultOwnerId(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
                  <option value="">Unassigned</option>
                  {(members ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {memberLabels.get(m.id) ?? m.displayName}
                    </option>
                  ))}
                </select>
              </label>

              {isOpportunity ? (
                <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
                  Default pipeline
                  <select value={defaultPipelineId} onChange={(e) => setDefaultPipelineId(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
                    <option value="">Tenant default</option>
                    {(pipelines ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
                    Default status
                    <select value={defaultStatusId} onChange={(e) => setDefaultStatusId(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
                      <option value="">Tenant default</option>
                      {(statuses ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
                    Tag all imported leads
                    <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Add tag…" className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal" />
                  </label>
                </>
              )}

              <div className="mt-auto flex items-start gap-2 rounded-input p-3" style={{ background: 'var(--color-primary-soft)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                <span className="text-xs leading-4.25" style={{ color: 'var(--color-primary-soft-text)' }}>
                  Every import is logged and can be rolled back for 7 days.
                </span>
              </div>
            </Card>

            <div className="col-span-2 flex shrink-0 items-center justify-end gap-2">
              <Button variant="secondary" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={onContinueToReview} loading={validateImport.isPending}>
                Continue to review
              </Button>
            </div>
          </div>
        )}

        {step === 'review' && job && (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
            <div className="grid shrink-0 grid-cols-4 gap-4">
              <StatCell label="Total rows" value={counts?.total ?? 0} />
              <StatCell label="Ready" value={counts?.valid ?? 0} tone="success" />
              <StatCell label="Duplicates" value={counts?.duplicate ?? 0} />
              <StatCell label="Invalid" value={counts?.invalid ?? 0} tone={counts && counts.invalid > 0 ? 'danger' : undefined} />
            </div>
            <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3">
                <h2 className="text-sm font-semibold text-ink">Rows needing a fix</h2>
                {(counts?.invalid ?? 0) > 0 && (
                  <a href={`/api/v1/imports/${job.id}/errors.csv`} className="flex items-center gap-1.5 text-[13px] font-semibold text-primary-soft-text hover:underline">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 4v12M7 11l5 5 5-5" />
                      <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                    </svg>
                    Download error report
                  </a>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {(detail?.errorRows.length ?? 0) === 0 ? (
                  <p className="p-5 text-sm text-text-muted">No invalid rows — every row is ready to import.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 border-b border-border bg-surface-subtle text-xs font-semibold text-text-muted">
                      <tr>
                        <th className="px-5 py-2 text-left">Row</th>
                        <th className="px-3 py-2 text-left">Data</th>
                        <th className="px-5 py-2 text-left">Why it failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail!.errorRows.map((r) => (
                        <tr key={r.rowNumber} className="border-b border-divider last:border-0">
                          <td className="px-5 py-2 tabular-nums text-text-secondary">{r.rowNumber}</td>
                          <td className="max-w-md truncate px-3 py-2 text-text-secondary">{Object.values(r.raw).join(', ')}</td>
                          <td className="px-5 py-2 text-danger">{r.errors?.join('; ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
            <div className="flex shrink-0 items-center justify-between">
              <span className="text-sm text-text-muted">
                {counts?.valid ?? 0} rows ready · {counts?.invalid ?? 0} need fixes
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setStep('map')}>
                  Back
                </Button>
                <Button onClick={onImport} loading={commit.isPending} disabled={(counts?.valid ?? 0) === 0}>
                  Import {counts?.valid ?? 0} rows
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'done' && job && job.status === 'completed' && (
          <Card className="flex flex-col gap-3 p-5">
            <h3 className="text-sm font-semibold">Import complete</h3>
            <ul className="text-sm text-text-secondary">
              <li>{job.counts.imported} created</li>
              <li>{job.counts.updated} updated</li>
              <li>{job.counts.duplicate} skipped as duplicates</li>
              <li>{job.counts.invalid} invalid rows</li>
              {job.counts.skipped > 0 && <li>{job.counts.skipped} blank rows skipped</li>}
            </ul>
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => rollback.mutate(job.id)} disabled={rollback.isPending} className="text-sm font-semibold text-danger hover:underline disabled:opacity-50">
                {rollback.isSuccess ? 'Rolled back' : 'Undo this import'}
              </button>
              <Button onClick={() => navigate({ to: isOpportunity ? '/deals' : '/leads' })}>{isOpportunity ? 'Go to opportunities' : 'Go to leads'}</Button>
            </div>
          </Card>
        )}
      </main>
    </>
  );
}

function StatCell({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'danger' }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">{label}</span>
      <span className={`text-2xl font-[650] tabular-nums ${tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-ink'}`}>{value}</span>
    </Card>
  );
}
